import { CHECKOUT_COPY } from '@eliadmin/core/src/components/dlz/checkout/checkout-copy';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type StatusState = 'done' | 'current' | 'upcoming';

/**
 * Where the track turns from the phone list into the row.
 * - `page`: from `md` – the confirmation page (Figma 60260:27705).
 * - `card`: once the nearest `@container` is 30rem wide – the account's order detail (Figma
 *   60317:25332); narrower the phone list of 60460:119839 (8 px gaps, the icon centred on the two
 *   lines of text, 8 px above and below the list).
 */
const LAYOUTS = {
  page: {
    list: 'flex w-full flex-col gap-4 md:flex-row md:py-2',
    item: 'flex min-w-0 flex-1 items-start gap-3 md:flex-col md:gap-2',
    track: 'contents md:flex md:w-full md:items-center md:gap-2',
    rowLine: 'hidden min-w-0 flex-1 md:flex',
    listLine: 'mt-2.5 flex min-w-0 flex-1 md:hidden',
  },
  card: {
    list: 'flex w-full flex-col gap-4 py-2 @[30rem]:flex-row',
    item: 'flex min-w-0 flex-1 items-center gap-2 @[30rem]:flex-col @[30rem]:items-start',
    track: 'contents @[30rem]:flex @[30rem]:w-full @[30rem]:items-center @[30rem]:gap-2',
    rowLine: 'hidden min-w-0 flex-1 @[30rem]:flex',
    listLine: 'flex min-w-0 flex-1 @[30rem]:hidden',
  },
} as const;

/** „dnes 14:32“, otherwise „12. 10. 14:32“. */
function receivedAt(createdAt: string) {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString('sk', { hour: '2-digit', minute: '2-digit' });
  const today = new Date().toDateString() === date.toDateString();
  return today
    ? `${CHECKOUT_COPY.confirmation.today} ${time}`
    : `${date.toLocaleDateString('sk', { day: 'numeric', month: 'numeric' })} ${time}`;
}

function StatusIcon({ state }: { state: StatusState }) {
  if (state === 'done') return <Icon name="check-circle" size={32} className="text-[#22A04B]" />;
  if (state === 'current') return <Icon name="clock" size={32} className="text-[#D7191F]" />;
  return <span aria-hidden="true" className="size-8 shrink-0 rounded-full bg-[#F5F6F7]" />;
}

/**
 * Figma „Order status“ (60260:27705; phones a list): 32 px state icon, the 2 px track (brand after
 * a finished state), name and time. The steps are received → being prepared → sent → delivered;
 * `current` is the index of the step in progress (right after ordering the order is received and
 * being prepared, so 1), the number of steps when the order is delivered. The store has no times
 * for the later steps, so only „Prijatá“ carries one.
 */
export function OrderStatus({
  createdAt,
  t,
  current = 1,
  layout = 'page',
}: {
  createdAt: string;
  t: TranslateFn;
  current?: number;
  layout?: keyof typeof LAYOUTS;
}) {
  const classes = LAYOUTS[layout];
  const state = (index: number): StatusState =>
    index < current ? 'done' : index === current ? 'current' : 'upcoming';
  const statuses = [
    { label: CHECKOUT_COPY.confirmation.received, note: receivedAt(createdAt) },
    { label: t('text3121') },
    { label: CHECKOUT_COPY.confirmation.sent },
    { label: CHECKOUT_COPY.confirmation.delivered },
  ].map((status, index) => ({ ...status, state: state(index) }));

  return (
    <ol className={classes.list}>
      {statuses.map((status, index) => {
        const last = index === statuses.length - 1;
        const line = (
          <span
            aria-hidden="true"
            className={cn(
              'h-0.5 min-w-0 flex-1',
              status.state === 'done' ? 'bg-[#D7191F]' : 'bg-[#E2E4E7]',
              last && 'invisible',
            )}
          />
        );
        return (
          <li
            key={status.label}
            aria-current={status.state === 'current' ? 'step' : undefined}
            className={classes.item}
          >
            <div className={classes.track}>
              <StatusIcon state={status.state} />
              <span className={classes.rowLine}>{line}</span>
            </div>
            <div className="flex shrink-0 flex-col gap-1 whitespace-nowrap">
              <p
                className={cn(
                  'dlz-body-s',
                  status.state === 'upcoming' ? 'text-[#6B7079]' : 'font-semibold text-[#0F1012]',
                )}
              >
                {status.label}
              </p>
              {status.note ? <p className="dlz-caption text-[#6B7079]">{status.note}</p> : null}
            </div>
            <span className={classes.listLine}>{line}</span>
          </li>
        );
      })}
    </ol>
  );
}
